import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  extractObject,
  generalConfigFromGlobal,
  mergeEnvToBaseUrls,
  resolveFilterParams,
  resolveGlobalParams,
  resolveRowPayload,
} from '../../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-bizz-hier-view',
    templateUrl: './bizz-hier-view.component.html',
    styleUrls: ['./bizz-hier-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BizzHierViewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  baseUrls: any = {};
  headerData: any = {};
  globalParams: any = {};
  filterParams: any = {};
  rowData: any = {};
  showActions = '';
  private adminApiUrl = '';
  private isInitialized = false;
  loading = false;
  collapsed = false;
  lastSyncedDate = '';

  activeTab: 'edit' | 'import' = 'edit';
  searchEdit = '';
  searchImport = '';
  editTree: any[] = [];
  importTree: any[] = [];
  currentTreeForImport: any[] = [];
  hierarchyId: any = null;

  treeCompared = false;
  onlyInImportCount = 0;
  onlyInCurrentCount = 0;
  movedCount = 0;

  showBuModal = false;
  editMode = false;
  modalNode: any = null;
  buCode = '';
  buDesc = '';
  buLob = 'IND';
  buDetailBsla = 'N';
  buEstDataCollection = 'N';

  minimiseRedirect = false;
  minimiseBu = false;
  minimiseImport = false;

  moveNodeRef: any = null;
  dragNodeRef: any = null;
  dragOverKey: any = null;
  dragOverRoot = false;

  showRedirectModal = false;
  sourceBuList: any[] = [];
  targetBuList: any[] = [];
  allBuList: any[] = [];
  redirectLoading = false;
  redirectDeleteLoading = false;
  redirectBtn = true;
  redirectDeleteBtn = true;
  showFormLoader = false;

  importFile: File | null = null;
  importFileName = '';
  importSkipHeader = true;
  importLoading = false;
  showImportTreeModal = false;
  importAlertMessage = 'No new import to display';
  currentAlertMessage = `Click 'Get Current Tree' to load data`;
  showConfirmDialog = false;
  confirmAction: 'delete-bu' | 'drop-move' | 'move-root' | 'redirect-delete' | null = null;
  confirmPayload: any = null;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonExportService: JSONExportService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseUrls = mergeEnvToBaseUrls({ ...gc }, this.gtwGlobal.GENERAL_CONFIG);
    this.filterParams = resolveFilterParams(this.adminScreenContext, this.gtwWorkspace);
    const rawRow = resolveRowPayload(this.adminScreenContext, this.gtwWorkspace);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    const hydrated = resolveGlobalParams(this.adminScreenContext, this.gtwGlobal, this.gtwWorkspace);
    this.globalParams = extractObject(hydrated);
    this.headerData = { globalParams: this.globalParams };
    this.showActions = String(this.globalParams?.is_locked ?? '');
    this.adminApiUrl =
      this.baseUrls?.admin_api_url || this.baseUrls?.admin || this.baseUrls?.base_url || '';
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    this.adminApiUrl = this.baseUrls?.admin_api_url || this.baseUrls?.admin || this.baseUrls?.base_url || '';
    const hasGlobalParams = !!this.globalParams && Object.keys(this.globalParams).length > 0;
    if (this.isInitialized || !this.adminApiUrl || !hasGlobalParams) {
      return;
    }
    this.isInitialized = true;
    this.loadEditTree();
  }

  private taxYear(): string {
    return String(this.globalParams?.tax_year ?? this.filterParams?.tax_year ?? this.rowData?.TAX_YEAR ?? '');
  }

  private scenario(): string {
    return String(this.globalParams?.scenario ?? this.filterParams?.scenario ?? this.rowData?.SCENARIO ?? '');
  }

  private jcdKey(): any {
    return this.globalParams?.jcd_key ?? this.filterParams?.jcd_key ?? this.rowData?.JCD_KEY ?? '';
  }

  private activityKey(): any {
    return this.globalParams?.activity_key
      ?? this.filterParams?.activity_key
      ?? this.rowData?.ACTIVITY_KEY
      ?? this.rowData?.activity_key
      ?? '';
  }

  tabChange(tab: 'edit' | 'import'): void {
    this.activeTab = tab;
    this.cdr.detectChanges();
  }

  loadEditTree(): void {
    this.loading = true;
    const url = `${this.adminApiUrl}/getBZHYUserPrivileges?tax_year=${this.taxYear()}&scenario=${this.scenario()}&jcd_key=${this.jcdKey()}&activity_key=${this.activityKey()}&access=1`;
    this.http.post<any>(url, null, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.loading = false;
        const data = res?.data ?? res;
        if (String(data?.callSuccess) === '1') {
          const nodes = data?.jsonObject || [];
          this.hierarchyId = data?.hierarchyId;
          this.editTree = this.ensureChildren(this.deepClone(nodes));
          this.allBuList = this.flattenForLookup(this.editTree);
          this.lastSyncedDate = String(nodes?.[0]?.dateTime ?? '');
        } else {
          this.editTree = [];
          this.lastSyncedDate = '';
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.editTree = [];
        this.gtwSnackbarService.openSnackBar('Failed to load business hierarchy', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  refreshEditTree(): void {
    this.loadEditTree();
  }

  isNodeVisible(node: any, mode: 'edit' | 'import'): boolean {
    const search = (mode === 'edit' ? this.searchEdit : this.searchImport).toLowerCase().trim();
    if (!search) {
      return true;
    }
    const name = String(this.nodeName(node) || '').toLowerCase();
    const code = String(this.nodeCode(node) || '').toLowerCase();
    return name.includes(search) || code.includes(search);
  }

  isDragOverNode(node: any): boolean {
    return String(this.dragOverKey || '') === String(node?.key || '');
  }

  toggleNode(node: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!node || !this.nodeChildren(node)?.length) {
      return;
    }
    node._collapsed = !node._collapsed;
  }

  isNodeCollapsed(node: any): boolean {
    return !!node?._collapsed;
  }

  openAddRoot(): void {
    this.editMode = false;
    this.modalNode = null;
    this.resetBuForm();
    this.minimiseBu = false;
    this.showBuModal = true;
  }

  openAddChild(node: any): void {
    this.editMode = false;
    this.modalNode = node;
    this.resetBuForm();
    this.minimiseBu = false;
    this.showBuModal = true;
  }

  openEdit(node: any): void {
    this.editMode = true;
    this.modalNode = node;
    this.buCode = String(node?.mekey || '');
    this.buDesc = String(node?.name || '');
    this.buLob = String(node?.line_of_business || 'IND');
    this.buDetailBsla = String(node?.detail_bsla_ind || 'N');
    this.buEstDataCollection = String(node?.EST_Data_coll_level || 'N');
    this.minimiseBu = false;
    this.showBuModal = true;
  }

  closeBuModal(): void {
    this.showBuModal = false;
  }

  submitBu(): void {
    if (!this.buCode || !this.buDesc) {
      this.gtwSnackbarService.openSnackBar('BU Code and BU Description are required', 'gtw-snackbar--yellow');
      return;
    }
    if (this.editMode && this.modalNode) {
      const payload = {
        meKey: this.modalNode?.key,
        groupObjKey: this.modalNode?.grpobjkey,
        description: this.buDesc,
        bUCode: this.buCode,
        line_of_business: this.buLob,
        detail_bsla_ind: this.buDetailBsla,
        srcHierarchyId: this.hierarchyId,
        hierarchyId: this.hierarchyId,
        taxYear: this.taxYear(),
        estDataCollLvl: this.buEstDataCollection,
        jcdKey: this.jcdKey(),
        scenarioId: this.scenario(),
        activity_key: this.activityKey(),
        access: 2
      };
      this.http.post<any>(`${this.adminApiUrl}/editBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (String(res?.result?.value ?? res?.value ?? '') === '1') {
            this.modalNode.name = this.buDesc;
            this.modalNode.mekey = this.buCode;
            this.modalNode.meCode = this.buCode;
            this.modalNode.line_of_business = this.buLob;
            this.modalNode.detail_bsla_ind = this.buDetailBsla;
            this.modalNode.EST_Data_coll_level = this.buEstDataCollection;
            this.allBuList = this.flattenForLookup(this.editTree);
            this.showBuModal = false;
            this.gtwSnackbarService.openSnackBar('Saved Business Unit Successfully', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar('Something went wrong, please try again', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Business Unit update failed', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
      return;
    }

    const payload = {
      meCode: this.buCode,
      meGoldName: 'None',
      meNonGoldName: this.buDesc,
      refreshIndicator: 'N',
      srcSystemFlag: 'TAX',
      scenarioId: this.scenario(),
      meParentKey: this.modalNode?.key || '',
      status: 'A',
      taxYear: this.taxYear(),
      jcdKey: this.jcdKey(),
      hierarchyId: this.hierarchyId,
      line_of_business: this.buLob,
      detail_bsla_ind: this.buDetailBsla,
      estDataCollLvl: this.buEstDataCollection,
      activity_key: this.activityKey(),
      access: 2
    };

    this.http.post<any>(`${this.adminApiUrl}/addBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (String(res?.callSuccess ?? '1') === '0') {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Business Unit creation failed', 'gtw-snackbar--red');
        } else {
          const newNode = {
            key: res?.p_me_key,
            mekey: this.buCode,
            name: this.buDesc,
            meCode: this.buCode,
            meParentKey: this.modalNode?.key || null,
            grpobjkey: Number(res?.p_group_obj_key || 0),
            line_of_business: this.buLob,
            detail_bsla_ind: this.buDetailBsla,
            EST_Data_coll_level: this.buEstDataCollection,
            children: []
          };
          if (this.modalNode) {
            this.modalNode.children = this.modalNode.children || [];
            this.modalNode.children.push(newNode);
          } else {
            this.editTree.unshift(newNode);
          }
          this.allBuList = this.flattenForLookup(this.editTree);
          this.showBuModal = false;
          this.gtwSnackbarService.openSnackBar('Created Business Unit Successfully', 'gtw-snackbar--green');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Business Unit creation failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  deleteBu(node: any, confirmed = false): void {
    if (!confirmed) {
      this.confirmAction = 'delete-bu';
      this.confirmPayload = node;
      this.confirmModalConfig = {
        ...this.confirmModalConfig,
        title: 'Delete Business Unit',
        message: 'Are you sure you want to delete a Business Unit?'
      };
      this.showConfirmDialog = true;
      this.cdr.detectChanges();
      return;
    }
    const payload = {
      meKey: node?.key,
      meParentKey: node?.meParentKey || '',
      groupObjKey: node?.grpobjkey,
      hierarchyId: this.hierarchyId,
      taxYear: this.taxYear(),
      jcdKey: this.jcdKey(),
      scenarioId: this.scenario(),
      activity_key: this.activityKey(),
      access: 2
    };
    this.http.post<any>(`${this.adminApiUrl}/deleteBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (String(res?.value) === '2') {
          this.gtwSnackbarService.openSnackBar('Cannot delete business as it is used in code combination.', 'gtw-snackbar--yellow');
        } else if (String(res?.callSuccess ?? '1') === '0') {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Business Unit deletion failed', 'gtw-snackbar--red');
        } else {
          this.removeNodeByKey(this.editTree, node?.key);
          this.allBuList = this.flattenForLookup(this.editTree);
          this.gtwSnackbarService.openSnackBar('Deleted Business Unit Successfully', 'gtw-snackbar--green');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Business Unit deletion failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  startMove(node: any): void {
    this.moveNodeRef = node;
    this.cdr.detectChanges();
  }

  cancelMove(): void {
    this.moveNodeRef = null;
    this.cdr.detectChanges();
  }

  isMovingSource(node: any): boolean {
    return !!this.moveNodeRef && !!node && String(this.moveNodeRef?.key) === String(node?.key);
  }

  canDropOnNode(node: any): boolean {
    if (!this.moveNodeRef || !node) {
      return false;
    }
    if (String(node?.key) === String(this.moveNodeRef?.key)) {
      return false;
    }
    return !this.isDescendantOf(this.moveNodeRef, node?.key);
  }

  private isDescendantOf(rootNode: any, key: any): boolean {
    if (!rootNode || key === undefined || key === null) {
      return false;
    }
    const children = Array.isArray(rootNode?.children) ? rootNode.children : [];
    for (const child of children) {
      if (String(child?.key) === String(key)) {
        return true;
      }
      if (this.isDescendantOf(child, key)) {
        return true;
      }
    }
    return false;
  }

  dropMoveOnNode(node: any, confirmed = false): void {
    if (!this.moveNodeRef || !node || String(this.moveNodeRef?.key) === String(node?.key)) {
      return;
    }
    if (!confirmed) {
      this.confirmAction = 'drop-move';
      this.confirmPayload = node;
      this.confirmModalConfig = {
        ...this.confirmModalConfig,
        title: 'Move Node',
        message: 'Are you sure you want to move this node?'
      };
      this.showConfirmDialog = true;
      this.cdr.detectChanges();
      return;
    }
    this.moveByApi(this.moveNodeRef, node?.key);
  }

  moveToRoot(node: any, confirmed = false): void {
    if (!confirmed) {
      this.confirmAction = 'move-root';
      this.confirmPayload = node;
      this.confirmModalConfig = {
        ...this.confirmModalConfig,
        title: 'Move Node',
        message: 'Are you sure want to make it as root node?'
      };
      this.showConfirmDialog = true;
      this.cdr.detectChanges();
      return;
    }
    this.moveByApi(this.moveNodeRef || node, '');
  }

  onDragStart(event: DragEvent, node: any): void {
    this.dragNodeRef = node;
    this.dragOverKey = null;
    this.dragOverRoot = false;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragOverNode(event: DragEvent, node: any): void {
    if (!this.dragNodeRef || !node || String(this.dragNodeRef?.key) === String(node?.key)) {
      return;
    }
    event.preventDefault();
    this.dragOverKey = node?.key;
  }

  onDropNode(event: DragEvent, node: any): void {
    event.preventDefault();
    if (!this.dragNodeRef || !node || String(this.dragNodeRef?.key) === String(node?.key)) {
      return;
    }
    this.dragOverKey = null;
    this.moveByApi(this.dragNodeRef, node?.key);
    this.dragNodeRef = null;
  }

  onDragOverRoot(event: DragEvent): void {
    if (!this.dragNodeRef) {
      return;
    }
    event.preventDefault();
    this.dragOverRoot = true;
  }

  onDropRoot(event: DragEvent): void {
    event.preventDefault();
    if (!this.dragNodeRef) {
      this.dragOverRoot = false;
      return;
    }
    this.dragOverRoot = false;
    this.moveByApi(this.dragNodeRef, '');
    this.dragNodeRef = null;
  }

  onDragEnd(): void {
    this.dragNodeRef = null;
    this.dragOverKey = null;
    this.dragOverRoot = false;
  }

  openRedirect(node: any): void {
    this.sourceBuList = this.flattenNode(node);
    this.targetBuList = this.sourceBuList.map(() => ({
      name: '-',
      mekey: '-',
      mecode: '-',
      taxyear: '-',
      targetSearchText: '',
      showList: false,
      selected: null,
      isTarget: false,
      showCancelBtn: false,
      loading: false,
      noRecords: false
    }));
    this.allBuList = this.flattenForLookup(this.editTree);
    this.updateRedirectButtonState();
    this.minimiseRedirect = false;
    this.showRedirectModal = true;
  }

  closeRedirect(): void {
    this.showRedirectModal = false;
  }

  searchTargets(index: number): void {
    const row = this.targetBuList[index];
    row.loading = false;
    row.noRecords = false;
    row.showCancelBtn = false;
    row.showList = !!row.targetSearchText;
  }

  pickTarget(index: number, target: any): void {
    const src = this.sourceBuList[index];
    if (String(src?.mekey) === String(target?.id)) {
      this.gtwSnackbarService.openSnackBar('Target BU cannot be same as source BU', 'gtw-snackbar--yellow');
      return;
    }
    this.targetBuList[index].name = target?.name;
    this.targetBuList[index].mekey = target?.id;
    this.targetBuList[index].mecode = target?.key || target?.id;
    this.targetBuList[index].taxyear = target?.taxyear || '-';
    this.targetBuList[index].selected = target;
    this.targetBuList[index].isTarget = true;
    this.targetBuList[index].showCancelBtn = true;
    this.targetBuList[index].targetSearchText = target?.name || '';
    this.targetBuList[index].showList = false;
    this.updateRedirectButtonState();
  }

  clearTarget(index: number): void {
    this.targetBuList[index].name = '-';
    this.targetBuList[index].mekey = '-';
    this.targetBuList[index].mecode = '-';
    this.targetBuList[index].taxyear = '-';
    this.targetBuList[index].selected = null;
    this.targetBuList[index].isTarget = false;
    this.targetBuList[index].showCancelBtn = false;
    this.targetBuList[index].targetSearchText = '';
    this.targetBuList[index].showList = false;
    this.updateRedirectButtonState();
  }

  saveRedirect(deleteAfter: boolean, confirmed = false): void {
    if (deleteAfter && !confirmed) {
      this.confirmAction = 'redirect-delete';
      this.confirmPayload = true;
      this.confirmModalConfig = {
        ...this.confirmModalConfig,
        title: 'Redirect and Delete Node',
        message: 'Are you sure want to redirect and delete the selected businesses from hierarchy?'
      };
      this.showConfirmDialog = true;
      this.cdr.detectChanges();
      return;
    }
    const sourceList: string[] = [];
    const targetList: string[] = [];
    const selectedCount = this.getSelectedTargetCount();
    if (deleteAfter && selectedCount !== this.targetBuList.length) {
      this.gtwSnackbarService.openSnackBar('Redirect and Delete requires target selection for all source businesses', 'gtw-snackbar--yellow');
      return;
    }
    this.targetBuList.forEach((t: any, idx: number) => {
      if (t?.selected) {
        sourceList.push(String(this.sourceBuList[idx]?.meCode ?? this.sourceBuList[idx]?.mekey ?? ''));
        targetList.push(String(t?.mecode ?? t.selected?.key ?? t.selected?.id ?? ''));
      }
    });
    if (sourceList.length === 0) {
      this.gtwSnackbarService.openSnackBar('No redirect target selected', 'gtw-snackbar--yellow');
      return;
    }
    if (!deleteAfter && selectedCount < this.targetBuList.length) {
      this.gtwSnackbarService.openSnackBar(
        'Only records with target selected will be saved. Remaining records will be ignored',
        'gtw-snackbar--yellow'
      );
    }
    if (deleteAfter) {
      this.redirectDeleteLoading = true;
    } else {
      this.redirectLoading = true;
    }

    const body: any = {
      tax_year: this.taxYear(),
      scenario: this.scenario(),
      jcd_key: this.jcdKey(),
      feedMeKeyList: sourceList.join(','),
      feedToMeKeyList: targetList.join(','),
      activity_key: this.activityKey(),
      access: 2
    };
    let url = `${this.adminApiUrl}/business/redirect`;
    if (deleteAfter) {
      body.hierarchyId = this.hierarchyId;
      url = `${this.adminApiUrl}/business/redirectAndDelete`;
    }

    this.http.post<any>(url, body, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.redirectLoading = false;
        this.redirectDeleteLoading = false;
        if (String(res?.callSuccess ?? '1') === '0') {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Business redirection failed', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Business redirection completed successfully', 'gtw-snackbar--green');
          this.showRedirectModal = false;
          this.loadEditTree();
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.redirectLoading = false;
        this.redirectDeleteLoading = false;
        this.gtwSnackbarService.openSnackBar('Business redirection failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  onConfirmAction(): void {
    const action = this.confirmAction;
    const payload = this.confirmPayload;
    this.showConfirmDialog = false;
    this.confirmAction = null;
    this.confirmPayload = null;

    if (action === 'delete-bu') {
      this.deleteBu(payload, true);
    } else if (action === 'drop-move') {
      this.dropMoveOnNode(payload, true);
    } else if (action === 'move-root') {
      this.moveToRoot(payload, true);
    } else if (action === 'redirect-delete') {
      this.saveRedirect(true, true);
    }
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.confirmAction = null;
    this.confirmPayload = null;
  }

  private getSelectedTargetCount(): number {
    return (this.targetBuList || []).filter((row: any) => !!row?.isTarget).length;
  }

  private updateRedirectButtonState(): void {
    const selected = this.getSelectedTargetCount();
    if (selected > 0 && selected < this.targetBuList.length) {
      this.redirectBtn = false;
      this.redirectDeleteBtn = true;
    } else if (selected === this.targetBuList.length && selected > 0) {
      this.redirectBtn = false;
      this.redirectDeleteBtn = false;
    } else {
      this.redirectBtn = true;
      this.redirectDeleteBtn = true;
    }
  }

  onImportFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.importFile = input?.files?.length ? input.files[0] : null;
    this.importFileName = this.importFile?.name || '';
    this.cdr.detectChanges();
  }

  runImport(closeModalOnSuccess = false): void {
    if (!this.importFile) {
      this.gtwSnackbarService.openSnackBar('Please select file to import', 'gtw-snackbar--yellow');
      return;
    }
    this.importLoading = true;
    const fd = new FormData();
    fd.append('file', this.importFile);
    const skipHeader = this.importSkipHeader ? 1 : 0;
    const url = `${this.adminApiUrl}/userUploadBizzHierarchy`;
    const params = new HttpParams()
      .set('fileName', this.importFile.name)
      .set('skipHeader', String(skipHeader))
      .set('activity_key', String(this.activityKey() ?? ''))
      .set('access', '2');
    this.http.post<any>(url, fd, { withCredentials: true, params }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.importLoading = false;
        if (String(res?.data?.callSuccess ?? res?.callSuccess ?? '') === '1') {
          const json = res?.data?.jsonObject ?? res?.jsonObject ?? '[]';
          this.importTree = this.ensureChildren(this.deepClone(JSON.parse(json)));
          this.importAlertMessage = this.importTree.length > 0 ? '' : 'There is no data available';
          this.refreshImportPresenceFlags();
          this.treeCompared = false;
          if (closeModalOnSuccess) {
            this.showImportTreeModal = false;
          }
          this.gtwSnackbarService.openSnackBar('Business hierarchy imported successfully', 'gtw-snackbar--green');
        } else {
          const msg = res?.data?.errorMessage || res?.errorMessage || 'Import failed';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.importLoading = false;
        this.gtwSnackbarService.openSnackBar('Import failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  loadCurrentTreeForImport(): void {
    this.currentTreeForImport = this.ensureChildren(this.deepClone(this.editTree));
    this.currentAlertMessage = this.currentTreeForImport.length > 0 ? '' : 'There is no data available';
    this.refreshImportPresenceFlags();
    this.treeCompared = false;
    this.cdr.detectChanges();
  }

  openHierarchyExcelModel(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.resetImportFileState();
    this.minimiseImport = false;
    this.showImportTreeModal = true;
  }

  closeImportTreeModal(): void {
    this.showImportTreeModal = false;
    this.resetImportFileState();
  }

  private resetImportFileState(): void {
    this.importFile = null;
    this.importFileName = '';
    this.importSkipHeader = true;
    const input = document.getElementById('import-bh-file-modal') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  confirmImportTree(): void {
    this.runImport(true);
  }

  openImportFileDialog(): void {
    const input = document.getElementById('import-bh-file-modal') as HTMLInputElement | null;
    if (input) {
      input.click();
    }
  }

  loadTree(side: 'right' | 'left'): void {
    if (side === 'right') {
      this.loadCurrentTreeForImport();
      return;
    }
    this.runImport();
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }

  compareImportVsCurrent(): void {
    if (!this.importTree?.length) {
      this.gtwSnackbarService.openSnackBar('Import a hierarchy first to compare with the current tree', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.currentTreeForImport?.length) {
      if (this.editTree?.length) {
        this.loadCurrentTreeForImport();
      } else {
        this.gtwSnackbarService.openSnackBar('Current tree is not available to compare', 'gtw-snackbar--yellow');
        return;
      }
    }

    this.resetCompareFlags(this.importTree);
    this.resetCompareFlags(this.currentTreeForImport);

    const imported = this.flattenByCode(this.importTree);
    const current = this.flattenByCode(this.currentTreeForImport);
    const importedCodes = Object.keys(imported);
    const currentCodes = Object.keys(current);
    const onlyInImport = importedCodes.filter((k) => !current[k]);
    const onlyInCurrent = currentCodes.filter((k) => !imported[k]);
    const importedParentByCode = this.buildParentCodeMap(this.importTree);
    const currentParentByCode = this.buildParentCodeMap(this.currentTreeForImport);
    const moved = this.findMovedCodes(currentParentByCode, importedParentByCode);

    this.applyFlagByCodes(this.importTree, new Set(onlyInImport), 'absentInRightTree');
    this.applyFlagByCodes(this.currentTreeForImport, new Set(onlyInCurrent), 'absentInLeftTree');
    this.applyFlagByCodes(this.currentTreeForImport, new Set(moved), 'nodeMoved');

    this.onlyInImportCount = onlyInImport.length;
    this.onlyInCurrentCount = onlyInCurrent.length;
    this.movedCount = moved.length;
    this.treeCompared = true;
    this.cdr.detectChanges();
  }

  autoPushFromImport(node: any): void {
    if (!this.hierarchyId) {
      return;
    }
    const list = this.flattenNode(node).map((x: any) => ({
      name: x.name,
      mekey: x.mekey,
      taxyear: x.taxyear,
      mecode: x.meCode
    }));
    const body = {
      json: JSON.stringify(list),
      tax_year: this.taxYear(),
      scenario: this.scenario(),
      jcd_key: this.jcdKey(),
      hierarchyId: this.hierarchyId,
      activity_key: this.activityKey(),
      access: 2
    };
    this.http.post<any>(`${this.adminApiUrl}/importExcelBizzHierarchy`, body, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.gtwSnackbarService.openSnackBar('Moved Business Unit Successfully', 'gtw-snackbar--green');
        this.loadEditTree();
        this.loadCurrentTreeForImport();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Auto push failed', 'gtw-snackbar--red');
      }
    });
  }

  exportTreeToExcel(mode: 'edit' | 'imported' | 'current'): void {
    let tree: any[] = [];
    if (mode === 'edit') {
      tree = this.editTree || [];
    } else if (mode === 'imported') {
      tree = this.importTree || [];
    } else {
      tree = this.currentTreeForImport || [];
    }

    if (!tree.length) {
      this.gtwSnackbarService.openSnackBar('No tree data available to export', 'gtw-snackbar--yellow');
      return;
    }

    const rows = this.flattenTreeForExcel(tree);
    const headers: JSONExportHeader[] = [
      { header: 'BU Code', field: 'bu_code' },
      { header: 'BU Name', field: 'bu_name' },
      { header: 'Parent BU Code', field: 'parent_bu' },
      { header: 'Line Of Business', field: 'line_of_business' },
      { header: 'Detail BSLA Ind', field: 'detail_bsla' },
      { header: 'Tax Year', field: 'tax_year' }
    ];
    this.jsonExportService.exportToExcelSharedStrings('Business_Hierarchy_List', rows, headers, undefined, 'Business_Hierarchy_List');
  }

  private moveByApi(node: any, newParentKey: any): void {
    const nodeToMove = this.moveNodeRef || node;
    const payload = {
      meKey: nodeToMove?.key,
      meParentKey: newParentKey || '',
      srcHierarchyId: this.hierarchyId,
      descHierarchyId: this.hierarchyId,
      tax_year: this.taxYear(),
      scenario: this.scenario(),
      jcd_key: this.jcdKey(),
      activity_key: this.activityKey(),
      access: 2
    };

    const formData = new URLSearchParams();
    Object.keys(payload).forEach((key: string) => {
      const value = (payload as any)[key];
      if (value !== undefined && value !== null) {
        formData.set(key, String(value));
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    this.http.post<any>(
      `${this.adminApiUrl}/moveBusinessHierarchy`,
      formData.toString(),
      { headers, withCredentials: true }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.moveNodeRef = null;
        this.dragNodeRef = null;
        this.gtwSnackbarService.openSnackBar('Moved Business Unit Successfully', 'gtw-snackbar--green');
        this.loadEditTree();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Move Business Unit failed', 'gtw-snackbar--red');
      }
    });
  }

  private flattenTreeForExcel(nodes: any[]): any[] {
    const result: any[] = [];
    const taxYearFallback = this.taxYear();
    const toTaxYearNumber = (raw: any): number | string => {
      const value = raw ?? taxYearFallback ?? '';
      const num = Number(value);
      return Number.isFinite(num) && String(value).trim() !== '' ? num : '';
    };
    const walk = (arr: any[], parentCode: string) => {
      (arr || []).forEach((n: any) => {
        const code = String(n?.mekey ?? n?.meCode ?? '');
        result.push({
          bu_code: code,
          bu_name: String(n?.name ?? n?.meName ?? ''),
          parent_bu: parentCode || code,
          line_of_business: String(n?.line_of_business ?? ''),
          detail_bsla: String(n?.detail_bsla_ind ?? ''),
          tax_year: toTaxYearNumber(n?.taxyear ?? n?.tax_year)
        });
        walk(n?.children || [], code);
      });
    };
    walk(nodes || [], '');
    return result;
  }

  private flattenByCode(nodes: any[]): Record<string, any> {
    const map: Record<string, any> = {};
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        const code = String(n?.mekey || n?.meCode || '');
        if (code) {
          map[code] = n;
        }
        walk(n?.children || []);
      });
    };
    walk(nodes || []);
    return map;
  }

  private refreshImportPresenceFlags(): void {
    const current = this.flattenByCode(this.currentTreeForImport);
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        const code = String(n?.mekey || n?.meCode || '');
        n.presentInrightTree = !!code && !current[code];
        walk(n?.children || []);
      });
    };
    walk(this.importTree);
  }

  private resetCompareFlags(nodes: any[]): void {
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        n.absentInRightTree = false;
        n.absentInLeftTree = false;
        n.nodeMoved = false;
        n.nodeEdited = false;
        walk(n?.children || []);
      });
    };
    walk(nodes || []);
  }

  private applyFlagByCodes(nodes: any[], codes: Set<string>, flagName: 'absentInRightTree' | 'absentInLeftTree' | 'nodeMoved'): void {
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        const code = String(n?.mekey || n?.meCode || '');
        n[flagName] = codes.has(code);
        walk(n?.children || []);
      });
    };
    walk(nodes || []);
  }

  private buildParentCodeMap(nodes: any[]): Record<string, string> {
    const map: Record<string, string> = {};
    const walk = (arr: any[], parentCode: string) => {
      (arr || []).forEach((n: any) => {
        const code = String(n?.mekey || n?.meCode || '');
        if (code) {
          map[code] = parentCode;
        }
        walk(n?.children || [], code);
      });
    };
    walk(nodes || [], '');
    return map;
  }

  private findMovedCodes(currentParentByCode: Record<string, string>, importedParentByCode: Record<string, string>): string[] {
    const moved: string[] = [];
    Object.keys(currentParentByCode).forEach((code: string) => {
      if (!(code in importedParentByCode)) {
        return;
      }
      if (currentParentByCode[code] !== importedParentByCode[code]) {
        moved.push(code);
      }
    });
    return moved;
  }

  private flattenForLookup(nodes: any[]): any[] {
    const out: any[] = [];
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        out.push({
          id: n?.mekey,
          key: n?.meCode || n?.mekey,
          name: n?.name,
          taxyear: n?.taxyear,
          label: `${n?.name || ''}-${n?.mekey || ''}`
        });
        walk(n?.children || []);
      });
    };
    walk(nodes || []);
    return out;
  }

  private flattenNode(node: any): any[] {
    const out: any[] = [];
    const walk = (n: any) => {
      out.push(n);
      (n?.children || []).forEach((c: any) => walk(c));
    };
    walk(node);
    return out;
  }

  private removeNodeByKey(nodes: any[], key: any): boolean {
    if (!nodes || nodes.length === 0) {
      return false;
    }
    const idx = nodes.findIndex((n: any) => String(n?.key) === String(key));
    if (idx >= 0) {
      nodes.splice(idx, 1);
      return true;
    }
    return nodes.some((n: any) => this.removeNodeByKey(n?.children || [], key));
  }

  private ensureChildren(nodes: any[]): any[] {
    const walk = (arr: any[]) => {
      (arr || []).forEach((n: any) => {
        n.children = Array.isArray(n.children) ? n.children : [];
        walk(n.children);
      });
    };
    walk(nodes || []);
    return nodes || [];
  }

  private deepClone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
  }

  private resetBuForm(): void {
    this.buCode = '';
    this.buDesc = '';
    this.buLob = 'IND';
    this.buDetailBsla = 'N';
    this.buEstDataCollection = 'N';
  }

  nodeName(node: any): string {
    return String(node?.name ?? node?.meName ?? '');
  }

  nodeCode(node: any): string {
    return String(node?.mekey ?? node?.meCode ?? '');
  }

  nodeChildren(node: any): any[] {
    return Array.isArray(node?.children) ? node.children : [];
  }

  isNodeEditable(node: any): boolean {
    if (this.showActions !== 'N') {
      return true;
    }
    return !!node?.isEdit;
  }
}

